import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  output,
  signal,
} from '@angular/core';
import { DvDynamicFormElement, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { AddressLookupResp } from '@shared/interfaces/address-lookup';
import { AddressSearchResultBoxComponent } from '@shared/components/address-search-result-box/address-search-result-box.component';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { AutoZoomFitParams, MapInput } from '@features/map/interfaces';
import { MapModule } from '@features/map/map.module';
import { AddressSearchService } from '@features/map/services/address-search.service';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounceTime,
  interval,
  of,
  skip,
  tap,
} from 'rxjs';
import { OnCallMapService } from './on-call-map.service';
import { OnCallFilteredItemsService } from '@pages/on-call-page/services/on-call-filtered-items.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { OnCallStatus } from '@pages/on-call-page/constants';
import { FormGroup } from '@angular/forms';
import {
  CustomOnCallMapSettings,
  OnCallDto,
} from '@pages/on-call-page/interfaces';
import { environment } from '@env/environment';
import { MapSetting } from '@features/map/constants/constants';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { MapId } from '@features/map/constants/map-id.enum';

@Component({
  imports: [
    MapModule,
    AsyncPipe,
    TimedMarkerMapMenuPanelComponent,
    AddressSearchResultBoxComponent,
    TranslateModule,
  ],
  selector: 'app-on-call-map',
  templateUrl: './on-call-map.component.html',
  styleUrls: ['./on-call-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnCallMapComponent implements OnInit {
  searchResult$: Observable<AddressLookupResp[]>;
  autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: false,
  });
  markers = signal<MapInput[] | undefined>(undefined);
  customMapSettingsForm: FormGroup<FormOf<CustomOnCallMapSettings>>;
  customMapSettingsElements: DvDynamicFormElement[];
  toggleMarkerClustering$ = new BehaviorSubject<boolean>(false);
  refreshTriggered = output<void>();

  refreshSubscription: Subscription | undefined;
  readonly mapSetting = MapSetting.OnCallMap;
  readonly mapId = MapId.OnCallMap;

  private destroyRef = inject(DestroyRef);
  private isShowOnlyStandbyEnabled: boolean;

  readonly splitterEvents = inject(DvSplitterEventsService);
  private addressSearchService = inject(AddressSearchService);
  private mapService = inject(OnCallMapService);
  private onCallGridItemSelectionService = inject(OnCallFilteredItemsService);

  ngOnInit(): void {
    this.setupEvents();
    this.setupMapSettingsForm();
  }

  onSearchAddressChanged(searchTerm: string | undefined): void {
    this.searchResult$ = searchTerm
      ? this.addressSearchService.getGPSPositionFromAddress({
          Address: searchTerm,
        })
      : of([]);
  }

  onAddressSelected(address: AddressLookupResp): void {
    if (!address.Position) return;
    this.autoZoomFit$.next({
      doAutoFit: true,
      coordinates: {
        lat: address.Position.Latitude,
        lng: address.Position.Longitude,
      },
    });
  }

  onShowOnlyStandbyClick(isOn: boolean): void {
    this.isShowOnlyStandbyEnabled = isOn;
    this.markers.set(
      this.mapService.getMapMarkers(
        this.getFilteredOnCallRows(),
        this.customMapSettingsForm.value
      )
    );
  }

  onAutoZoomFitClicked(isAutoZoomOn: boolean): void {
    this.autoZoomFit$.next({ doAutoFit: isAutoZoomOn });
  }

  onGroupMarkersClicked(isClusteringOn: boolean): void {
    this.toggleMarkerClustering$.next(isClusteringOn);
  }

  onCustomSettingsChanged(settings: CustomOnCallMapSettings): void {
    this.markers.set(
      this.mapService.getMapMarkers(this.getFilteredOnCallRows(), settings)
    );
  }

  onAutoRefreshClicked(isAutoRefreshOn: boolean): void {
    if (isAutoRefreshOn) {
      this.refreshSubscription = interval(environment.mapRefreshInterval)
        .pipe(
          tap(() => this.refreshTriggered.emit()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.refreshSubscription?.unsubscribe();
    }
  }

  private setupEvents(): void {
    this.onCallGridItemSelectionService.onCallGridFilterChanged$
      // skipping first emission since it's default BehaviorSubject value
      .pipe(skip(1), debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ items, source }) => {
        const isAutoFitOn = this.autoZoomFit$.getValue().doAutoFit;
        this.markers.set(
          this.mapService.getMapMarkers(
            this.getFilteredOnCallRows(items),
            this.customMapSettingsForm.value
          )
        );

        if (
          !source ||
          source === 'columnFilter' ||
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          (source === 'rowDataUpdated' && isAutoFitOn)
        ) {
          setTimeout(() => {
            this.autoZoomFit$.next({ doAutoFit: true });
            if (!isAutoFitOn) {
              this.autoZoomFit$.next({ doAutoFit: false });
            }
          });
        }
      });
  }

  private setupMapSettingsForm(): void {
    this.customMapSettingsElements =
      this.mapService.getCustomMapSettingsElements();
    this.customMapSettingsForm = DvFormUtils.createFormGroup(
      this.customMapSettingsElements
    );
  }

  private getFilteredOnCallRows(items?: OnCallDto[]): OnCallDto[] {
    return (
      items ||
      this.onCallGridItemSelectionService.onCallGridFilterChanged$.getValue()
        .items
    ).filter(val => {
      if (this.isShowOnlyStandbyEnabled) {
        return !!val.IsLocation && val.OnCallStatus === OnCallStatus.OnCall;
      }
      return true;
    });
  }
}
